import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Quote, Heart, Download, Users, Webhook } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { analytics } from '../lib/analytics-events';
import { 
  faInstagram, 
  faThreads, 
  faSpotify, 
  faAmazon,
  faMedium
} from '@fortawesome/free-brands-svg-icons';

const features = [
  {
    icon: Quote,
    title: 'Capture Memories',
    description: 'Save meaningful quotes from family, friends or anyone in one secure place.'
  },
  {
    icon: Heart,
    title: 'Search and Filter',
    description: 'Search and filter your quotes by author, favorites and quote wall.'
  },
  {
    icon: Download,
    title: 'Easy Export',
    description: 'Download your quotes in various formats for safekeeping.'
  },
  {
    icon: Users,
    title: 'Share Moments',
    description: 'Share selected quotes with anyone privately or on our community quote wall.'
  }
];

const socialLinks = [
  {
    name: 'Instagram',
    icon: faInstagram,
    url: 'https://www.instagram.com/mygoodquotes_dlmc/'
  },
  {
    name: 'Threads',
    icon: faThreads,
    url: 'https://threads.net/@mygoodquotes_dlmc'
  },
  {
    name: 'Spotify',
    icon: faSpotify,
    url: 'https://open.spotify.com/show/7waHZNzH5g3Ywx5x7RMveX'
  },
  {
    name: 'Amazon',
    icon: faAmazon,
    url: 'https://www.amazon.com/dp/B0DL61M6PH'
  },
  {
    name: 'Medium',
    icon: faMedium,
    url: 'https://medium.com/@mygoodquotes'
  }
];

export default function LandingPage() {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      {/* Navigation */}
      <nav className="relative z-10">
        <div className="container-praise py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <img 
              src="https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/mgqlogo2.png"
              alt="MyGoodQuotes Logo"
              className="h-12 sm:h-16 w-auto"
            />
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button 
                onClick={() => navigate('/about')}
                className="text-gray-600 hover:text-gray-900 font-medium px-3 py-2"
              >
                About
              </button>
              <button 
                onClick={() => navigate('/overview')}
                className="text-gray-600 hover:text-gray-900 font-medium px-3 py-2"
              >
                Overview
              </button>
              <button
                onClick={() => {
                  analytics.trackButtonClick('login_nav', {
                    event_category: 'navigation',
                    button_location: 'header',
                  });
                  navigate('/login');
                }}
                className="btn-primary px-4 py-2"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <motion.div 
        className="container-praise pt-12 sm:pt-20 pb-16 text-center px-4 sm:px-6"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div 
          className="max-w-3xl mx-auto"
          variants={itemVariants}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-50 rounded-full text-brand-600 font-medium mb-8">
            <Sparkles className="h-4 w-4" />
            <span>Now in early access</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-gray-900 mb-6 tracking-tight">
            Keep your favorite quotes&nbsp;
            <br className="hidden sm:block" />
            all in one place
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-12 px-4">
            MyGoodQuotes helps you capture, save and share quotes
            <br className="hidden sm:block" />
            from family, friends or anyone.
          </p>

          {/* Preview Image */}
          <motion.div 
            className="mb-12 px-4 sm:px-0"
            variants={itemVariants}
          >
            <img
              src="https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/homepage1.png"
              alt="MyGoodQuotes Interface"
              className="rounded-xl shadow-praise mx-auto"
            />
          </motion.div>

          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center px-4"
            variants={itemVariants}
          >
            <button
              onClick={() => navigate('/early-access')}
              className="btn-primary text-lg px-6 sm:px-8 py-3 w-full sm:w-auto"
            >
              Get Early Access
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={() => navigate('/demo')}
              className="btn-secondary text-lg px-6 sm:px-8 py-3 w-full sm:w-auto"
            >
              Watch Demo
            </button>
          </motion.div>
        </motion.div>

        {/* Feature Grid */}
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mt-24 px-4 sm:px-6"
          variants={containerVariants}
        >
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                className="card p-6"
                variants={itemVariants}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-brand-50 text-brand-600 mb-4">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Discover Quotes Section */}
        {/* Discover Quotes Section - Hidden for now */}
        {/* 
        <motion.div 
          className="mt-24 text-center px-4 sm:px-6"
          variants={itemVariants}
        >
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 mb-4">
            Want to discover new quotes for your collection?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Try our free service and find quotes you like.
          </p>
          <a
            href="https://find.mygoodquotes.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-lg px-6 sm:px-8 py-3 inline-flex items-center justify-center"
          >
            Discover Quotes
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </motion.div>
        */}
      </motion.div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="container-praise py-8 px-4">
          <div className="flex flex-col items-center text-center">
            {/* Logo and Description */}
            <img 
              src="https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/mgqlogo2.png"
              alt="MyGoodQuotes Logo"
              className="h-12 w-auto mb-2"
            />
            <p className="text-gray-600 mb-4">
              Keep all your favorite quotes in one place.
            </p>

            {/* Social Links */}
            <div className="flex flex-wrap justify-center gap-6 mb-6">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                  aria-label={social.name}
                >
                  <FontAwesomeIcon icon={social.icon} className="h-6 w-6" />
                </a>
              ))}
              <button
                onClick={() => navigate('/api')}
                className="text-gray-600 hover:text-gray-900 transition-colors"
                aria-label="API Documentation"
              >
                <Webhook className="h-6 w-6" />
              </button>
            </div>

            {/* Langston Hughes Quote */}
            <div className="max-w-2xl mx-auto mt-8 mb-8 px-4">
              <blockquote className="italic text-gray-700 text-lg">
                "Hold fast to dreams, for if dreams die, life is a broken-winged bird that cannot fly."
              </blockquote>
              <p className="mt-2 text-gray-600">—Langston Hughes</p>
            </div>

            {/* Copyright */}
            <div className="mt-4 pt-4 border-t border-gray-200 w-full">
              <p className="text-center text-gray-600">
                © {new Date().getFullYear()} MyGoodQuotes. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
